/**
 * React hooks wrapping the API client. Thin layer over `getEndpoints()` /
 * `getEndpoint()` so components don't import the spec parser directly and so
 * we can swap in async loading later (e.g., fetch openapi.json over HTTP
 * instead of bundling it).
 */

import { useMemo } from 'react';
import {
  getEndpoints,
  getEndpoint,
  getEndpointByPathMethod,
  getTags,
  type EndpointInfo,
} from './client';

export function useEndpoints(): EndpointInfo[] {
  // Memo not strictly needed today (sync, cached) but locks down the contract
  // if we later make `getEndpoints` async.
  return useMemo(() => getEndpoints(), []);
}

export function useEndpoint(operationId: string | undefined): EndpointInfo | null {
  return useMemo(() => (operationId ? getEndpoint(operationId) : null), [operationId]);
}

export function useEndpointByPathMethod(path: string, method: string): EndpointInfo | null {
  return useMemo(() => getEndpointByPathMethod(path, method), [path, method]);
}

export function useTags(): { name: string; count: number }[] {
  return useMemo(() => getTags(), []);
}

/**
 * Group endpoints by tag — used by sidebar to render collapsible sections.
 */
export function useEndpointsByTag(): Map<string, EndpointInfo[]> {
  return useMemo(() => {
    const map = new Map<string, EndpointInfo[]>();
    for (const ep of getEndpoints()) {
      const list = map.get(ep.tag) ?? [];
      list.push(ep);
      map.set(ep.tag, list);
    }
    return map;
  }, []);
}
